/**
 * w2ui - w2_05. Shared look for the Worthly 2.0 sign-up screens, setup steps
 * and walkthrough: lavender page, white cards, soft pastels, black buttons.
 */

export const W2 = {
  page: '#F3F0FA', ink: '#25222B', soft: '#8E8A99', faint: '#ABA6B8', line: '#EFECF5', field: '#F7F5FB',
  lil: '#ECE6FA', lilT: '#7A5DD3', lilBar: '#B9A6F0', lilDot: '#8E74E3',
  mint: '#DFF2E7', mintT: '#2F8756', mintBar: '#9ED9B6',
  blue: '#DFE9F9', blueT: '#3C6DB5', blueBar: '#A9C6F0',
  pink: '#FAE4ED', pinkT: '#C04B7C', pinkBar: '#F2AFC8',
  butter: '#FCF3D2', butterT: '#8A7A3C', butterBar: '#F3DE8E',
  button: '#1F1D24',
};
export const W2_FONT = "'DM Sans', -apple-system, 'Helvetica Neue', sans-serif"; // w2_11

export const w2Title = { margin: 0, fontSize: 28, fontWeight: 600, letterSpacing: '-0.045em', lineHeight: 1.08, color: W2.ink };
export const w2Sub = { margin: '8px 0 0', fontSize: 14, color: W2.soft, lineHeight: 1.5 };
export const w2Card = { background: '#FFFFFF', borderRadius: 26, padding: 18 };
export const w2Lab = { display: 'block', fontSize: 12, fontWeight: 600, color: W2.faint, letterSpacing: '0.05em', margin: '18px 4px 8px' };
export const w2Field = {
  display: 'block', width: '100%', minWidth: 0, background: '#FFFFFF', border: 0, borderRadius: 16, padding: '13px 15px',
  fontSize: 16, color: W2.ink, outline: 'none', fontFamily: W2_FONT, WebkitAppearance: 'none', appearance: 'none',
};
export const w2Btn = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', border: 0, borderRadius: 999,
  padding: 15, fontSize: 15, fontWeight: 600, background: W2.button, color: '#FFFFFF', fontFamily: W2_FONT,
  WebkitTapHighlightColor: 'transparent', cursor: 'pointer',
};
export const w2WhiteBtn = { ...w2Btn, background: '#FFFFFF', color: W2.ink, boxShadow: `inset 0 0 0 1px ${W2.line}` };
export const w2BackBtn = {
  flex: 'none', border: 0, borderRadius: 999, padding: '15px 20px', background: '#FFFFFF', fontSize: 14.5, fontWeight: 600,
  color: W2.ink, fontFamily: W2_FONT, WebkitTapHighlightColor: 'transparent', cursor: 'pointer',
};
export const w2Dashed = {
  display: 'block', width: '100%', background: 'transparent', border: '2px dashed #DCD3F4', borderRadius: 999, padding: 12,
  fontSize: 14, fontWeight: 600, color: W2.lilT, marginTop: 10, fontFamily: W2_FONT, cursor: 'pointer',
};
export const w2Count = { textAlign: 'center', fontSize: 12, color: W2.faint, margin: '12px 0 0' };

// Full-screen page: content scrolls, the footer stays at the bottom.
// w2_06: footPad lifts the footer, e.g. above the tab bar.
// w2_07: bg and backdrop (drawn behind everything, e.g. a soft glow).
export const W2Page = ({ children, foot, testId, bodyStyle, footPad, bg, backdrop }) => (
  <div
    className="fixed inset-0 flex flex-col"
    style={{
      background: bg || W2.page, fontFamily: W2_FONT, color: W2.ink,
      paddingTop: 'max(env(safe-area-inset-top), 20px)',
      paddingLeft: 'env(safe-area-inset-left)', paddingRight: 'env(safe-area-inset-right)',
    }}
    data-testid={testId}
  >
    {backdrop && <div aria-hidden="true" style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>{backdrop}</div>}
    <div style={{ position: 'relative', zIndex: 1, flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}>
      <div style={{ maxWidth: 460, margin: '0 auto', padding: '14px 18px 18px', ...bodyStyle }}>{children}</div>
    </div>
    {foot && (
      <div style={{ position: 'relative', zIndex: 1, flex: 'none', padding: '10px 18px 0', paddingBottom: footPad || 'calc(env(safe-area-inset-bottom, 0px) + 18px)' }}>
        <div style={{ maxWidth: 460, margin: '0 auto' }}>{foot}</div>
      </div>
    )}
  </div>
);

export const W2Bubble = ({ bg, e, size = 50 }) => (
  <span className="flex items-center justify-center flex-shrink-0" style={{ width: size, height: size, borderRadius: 999, background: bg, fontSize: Math.round(size * 0.46) }}>{e}</span>
);

export const W2Switch = ({ on, onClick, label, testId }) => (
  <button
    type="button"
    role="switch"
    aria-checked={!!on}
    aria-label={label}
    onClick={onClick}
    data-testid={testId}
    style={{ position: 'relative', width: 44, height: 26, borderRadius: 999, border: 0, padding: 0, flexShrink: 0, background: on ? W2.mintBar : '#E4E0EC', transition: 'background .15s', cursor: 'pointer' }}
  >
    <span style={{ position: 'absolute', top: 3, left: on ? 21 : 3, width: 20, height: 20, borderRadius: 999, background: '#FFFFFF', boxShadow: '0 1px 3px rgba(0,0,0,0.15)', transition: 'left .15s' }} />
  </button>
);

// A row with a title, a small line under it and a switch.
export const W2SwitchRow = ({ emoji, title, sub, on, onToggle, first, testId }) => (
  <div className="flex items-center" style={{ gap: 12, padding: '12px 0', borderTop: first ? 0 : `1px solid ${W2.line}` }}>
    {emoji && <span style={{ fontSize: 20, flexShrink: 0 }}>{emoji}</span>}
    <span style={{ flex: 1, minWidth: 0 }}>
      <span style={{ display: 'block', fontSize: 14.5, fontWeight: 600, color: W2.ink }}>{title}</span>
      {sub && <span style={{ display: 'block', fontSize: 12.5, color: W2.soft, marginTop: 1, lineHeight: 1.4 }}>{sub}</span>}
    </span>
    <W2Switch on={on} onClick={onToggle} label={title} testId={testId} />
  </div>
);

// One feature line: soft square emoji, bold title, small line.
export const W2Feat = ({ e, bg, title, text, first }) => (
  <div className="flex items-start" style={{ gap: 12, padding: '11px 0', borderTop: first ? 0 : `1px solid ${W2.line}` }}>
    <span className="flex items-center justify-center flex-shrink-0" style={{ width: 40, height: 40, borderRadius: 14, background: bg, fontSize: 18 }}>{e}</span>
    <span style={{ minWidth: 0 }}>
      <span style={{ display: 'block', fontSize: 14.5, fontWeight: 600, color: W2.ink }}>{title}</span>
      <span style={{ display: 'block', fontSize: 13, color: W2.soft, lineHeight: 1.45, marginTop: 1 }}>{text}</span>
    </span>
  </div>
);

export const W2Dots = ({ count, at }) => (
  <div className="flex" style={{ gap: 6 }}>
    {Array.from({ length: count }).map((_, i) => (
      <span key={i} style={{ width: i === at ? 20 : 7, height: 7, borderRadius: 9, background: i === at ? W2.lilDot : '#D9D2EE', transition: 'width .2s' }} />
    ))}
  </div>
);
